// components/sections/hero.jsx
'use client';

import Image from 'next/image';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

import ContactActionButtons from '@/components/contact-actions';
import Link from 'next/link';

const ease = [0.22, 1, 0.36, 1];

const container = {
    hidden: {},
    show: {
        transition: { staggerChildren: 0.12, delayChildren: 0.05 },
    },
};

const fadeUp = {
    hidden: { opacity: 0, y: 18, filter: 'blur(6px)' },
    show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.7, ease } },
};

const fadeLeft = {
    hidden: { opacity: 0, x: 18, filter: 'blur(6px)' },
    show: { opacity: 1, x: 0, filter: 'blur(0px)', transition: { duration: 0.75, ease } },
};

const floaty = {
    hidden: { opacity: 0, y: -10, rotate: 18 },
    show: {
        opacity: 1,
        y: 0,
        rotate: 30,
        transition: { duration: 0.9, ease },
    },
};

export default function Hero({
    bgImage,
    BgclassName,
    titleClassName,
    title = (
        <>
            <span className="text-secondary">Create. Publish. Inspire </span>Readers Everywhere.
        </>
    ),
    description = 'At Virginia Book Publisher, we help writers turn ideas into impactful books. From editing and cover design to distribution and marketing, we handle the full publishing process—so you can focus on writing. Let’s turn your manuscript into a book readers will remember.',
    subTitle = 'Empowering Authors Worldwide',
    contentWrapper,

    // layout
    reverse = false,
    className,

    // preview controls
    previewNodeClass,
    showPreview = true,
    preview,
    previewImage,
    overlay = true,

    children,
}) {
    const previewNode = children ?? preview;

    return (
        <section
            className={cn(
                'border-b bg-[#060340] bg-cover bg-center bg-no-repeat pt-24 md:pt-24 lg:pt-[180px]',
                className
            )}
            style={{ backgroundImage: 'url(/waveelement2.png)' }}
        >
            {bgImage && (
                <div
                    className={cn(
                        'absolute left-0 right-0 top-0 w-full h-full pointer-events-none bg-cover bg-center opacity-10',
                        BgclassName
                    )}
                    style={{ backgroundImage: `url(${bgImage})` }}
                ></div>
            )}
            {overlay && (
                <div className="absolute left-0 right-0 top-0 w-full h-full bg-white/10 pointer-events-none"></div>
            )}

            <div className="container">
                <motion.div
                    variants={container}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.35 }}
                    className={cn(
                        'items-center lg:flex lg:flex-row flex-col gap-14 lg:gap-[152px]',
                        reverse && 'lg:[&>*:first-child]:order-2 lg:[&>*:last-child]:order-1',
                        contentWrapper
                    )}
                >
                    {/* LEFT: content */}
                    <motion.div variants={container} className="space-y-6 relative">
                        {/* Floating glasses */}
                        {/* <motion.div
                            variants={floaty}
                            className="w-[360px] absolute -right-[107px] -top-18 rotate-12 glasses-animate"
                            animate={{ y: [0, -8, 0] }}
                            transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut' }}
                        >
                            <Image
                                src="/glasses.svg"
                                alt="Ebook Publishing"
                                width={388}
                                height={186}
                                priority
                            />
                        </motion.div> */}

                        <motion.span variants={fadeUp} className="text-white/60 fs-30 mb-1.5 block">
                            {subTitle}
                        </motion.span>

                        <motion.h1
                            variants={fadeUp}
                            className={cn(
                                'text-balance text-white font-semibold fs-54',
                                titleClassName
                            )}
                        >
                            {title}
                        </motion.h1>

                        <motion.p
                            variants={fadeUp}
                            className="text-pretty text-white/80 text-base sm:text-lg"
                        >
                            {description}
                        </motion.p>

                        {/* CTA */}
                        <motion.div
                            variants={fadeUp}
                            className="flex flex-col gap-3 sm:flex-row sm:items-center max-lg:mb-14"
                        >
                            <ContactActionButtons
                                show={['chat', 'call']}
                                onChat={() => window?.Tawk_API?.maximize?.()} // example for tawk
                                phone="+15408938895"
                                chatVariant="secondary"
                                callVariant="outline"
                                size="3xl"
                                classBtn1="max-sm:w-full"
                                classBtn2="max-sm:w-full"
                            />
                            {/* <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }}>
                                <Button asChild size="2xl" rounded="full">
                                    <Link
                                        className="decoration-0 hover:no-underline"
                                        href={primaryCta.href}
                                    >
                                        {primaryCta.label}
                                    </Link>
                                </Button>
                            </motion.div> */}
                            {/* <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }}>
                                {' '}
                                <Link
                                    className="decoration-0 hover:no-underline"
                                    href={secondaryCta.href}
                                >
                                    <Button variant="outline" size="2xl" rounded="full">
                                        {secondaryCta.label}
                                    </Button>
                                </Link>
                            </motion.div> */}
                        </motion.div>
                        <div className="flex gap-5 items-center mt-10">
                            {/* Review.io */}
                            {/* <Link
                                href="https://www.reviews.io/company-reviews/store/virginiabookpublisher.com-2no1qng"
                                target="_blank"
                            >
                                <img
                                    src="/reviews/review.png"
                                    alt="Reviews.io"
                                    className="h-14 w-auto hover:opacity-80 transition"
                                />
                            </Link> */}

                            {/* Bark */}
                            {/* <Link
                                href="https://www.bark.com/en/us/company/virginia-book-publisher/n7dKy9/?review_source=share_link"
                                target="_blank"
                            >
                                <img
                                    src="/reviews/bark.png"
                                    alt="Bark"
                                    className="h-10 w-auto hover:opacity-80 transition"
                                />
                            </Link> */}
                        </div>
                    </motion.div>

                    {/* RIGHT: preview */}
                    {showPreview ? (
                        <motion.div
                            variants={fadeLeft}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, amount: 0.35 }}
                            className="relative w-full lg:max-w-[350px] xl:max-w-[470px] 2xl:max-w-[550px] shrink-0 ml-auto"
                        >
                            {/* glow */}
                            <motion.div
                                aria-hidden="true"
                                className="absolute -inset-2 -z-10 rounded-2xl bg-linear-to-r from-muted/40 to-muted/10 blur-2xl"
                                animate={{ opacity: [0.55, 0.85, 0.55] }}
                                transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut' }}
                            />

                            <motion.div
                                className={cn(
                                    'rounded-2xl border bg-background shadow-sm',
                                    previewNodeClass
                                )}
                                whileHover={{ y: -4 }}
                                transition={{ duration: 0.25, ease }}
                            >
                                <div>
                                    {previewNode ? (
                                        <div className="min-h-[220px]">
                                            {/* <div className="absolute rounded-lg w-[117%] h-[70%] bg-blue-800/80 z-5 top-[15%] left-[-8.5%]"></div>
                                            <div className="absolute rounded-lg w-[110%] h-[60%] bg-blue-700/70 z-6 top-[20%] left-[-5%]"></div> */}
                                            <div className="relative z-10">{previewNode}</div>
                                        </div>
                                    ) : previewImage ? (
                                        <div className="relative aspect-16/10 w-full overflow-hidden rounded-xl border bg-muted">
                                            <Image
                                                src={previewImage}
                                                alt="Hero preview"
                                                fill
                                                className="object-cover"
                                                priority
                                            />
                                        </div>
                                    ) : (
                                        <div className="space-y-3 p-6">
                                            <div className="h-10 w-full rounded-md bg-muted/60" />
                                            <div className="h-24 w-full rounded-md bg-muted/60" />
                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="h-16 rounded-md bg-muted/60" />
                                                <div className="h-16 rounded-md bg-muted/60" />
                                            </div>
                                            <div className="h-10 w-32 rounded-md bg-muted/60" />
                                            <p className="pt-1 text-xs text-muted-foreground">
                                                Lightweight skeleton preview (no heavy assets).
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        </motion.div>
                    ) : null}
                </motion.div>
            </div>
        </section>
    );
}
